'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { rfqAnalytics } from '@/lib/analytics';

type Step = 1 | 2 | 3 | 4 | 5;

interface RFQFormData {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactDesignation: string;
  companyName: string;
  companyWebsite: string;
  country: string;
  partName: string;
  partNumber: string;
  drawingRevision: string;
  quantityRequired: string;
  unitOfMeasure: string;
  drawingAvailable: boolean;
  drawingFormat: string;
  materialType: string;
  materialGrade: string;
  manufacturingProcesses: string[];
  surfaceFinish: string;
  heatTreatment: string;
  generalTolerance: string;
  criticalDimensions: string;
  geometricTolerances: string;
  inspectionRequirements: string;
  requiredCertifications: string[];
  qualityStandard: string;
  firstArticleRequired: boolean;
  materialCertificationRequired: boolean;
  requiredDeliveryDate: string;
  deliveryTerms: string;
  deliveryLocation: string;
  urgencyLevel: string;
  targetUnitPrice: string;
  currency: string;
  annualVolumeEstimate: string;
  specialRequirements: string;
  packagingRequirements: string;
  additionalNotes: string;
}

const initialForm: RFQFormData = {
  contactName: '', contactEmail: '', contactPhone: '', contactDesignation: '',
  companyName: '', companyWebsite: '', country: '',
  partName: '', partNumber: '', drawingRevision: '', quantityRequired: '',
  unitOfMeasure: 'pieces', drawingAvailable: false, drawingFormat: '',
  materialType: '', materialGrade: '', manufacturingProcesses: [],
  surfaceFinish: '', heatTreatment: '',
  generalTolerance: '', criticalDimensions: '', geometricTolerances: '',
  inspectionRequirements: '', requiredCertifications: [], qualityStandard: '',
  firstArticleRequired: false, materialCertificationRequired: false,
  requiredDeliveryDate: '', deliveryTerms: '', deliveryLocation: '',
  urgencyLevel: 'standard', targetUnitPrice: '', currency: 'USD',
  annualVolumeEstimate: '', specialRequirements: '', packagingRequirements: '',
  additionalNotes: '',
};

const MANUFACTURING_PROCESSES = [
  'CNC Machining', '5-Axis Machining', 'Turning / Lathe', 'Milling',
  'Sheet Metal Fabrication', 'Welding / Brazing', 'Casting', 'Forging',
  'Additive Manufacturing / 3D Printing', 'EDM / Wire EDM', 'Grinding',
  'Laser Cutting', 'Waterjet Cutting', 'Assembly / Sub-Assembly', 'Other',
];

const CERTIFICATIONS = [
  'AS9100D', 'ISO 9001:2015', 'NADCAP', 'ITAR', 'ISO 14001',
  'ISO 45001', 'DFARS Compliant', 'MIL-SPEC', 'REACH / RoHS', 'Other',
];

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Germany', 'France',
  'Japan', 'South Korea', 'Australia', 'Canada', 'Singapore',
  'UAE', 'Israel', 'Italy', 'Spain', 'Netherlands', 'Other',
];

const MATERIAL_TYPES = [
  'Aluminum Alloy', 'Titanium Alloy', 'Stainless Steel', 'Carbon Steel',
  'Alloy Steel', 'Inconel / Superalloy', 'Composite (CFRP/GFRP)',
  'Copper / Brass', 'Magnesium Alloy', 'Plastics / Polymers', 'Other',
];

const stepTitles: Record<Step, string> = {
  1: 'Contact & Company',
  2: 'Part / Component Details',
  3: 'Material & Manufacturing',
  4: 'Tolerances & Quality',
  5: 'Delivery & Commercial',
};

const stepIcons: Record<Step, string> = {
  1: 'UserIcon',
  2: 'CubeIcon',
  3: 'WrenchScrewdriverIcon',
  4: 'ShieldCheckIcon',
  5: 'TruckIcon',
};

const stepDescriptions: Record<Step, string> = {
  1: 'Your contact information and company details',
  2: 'Part name, number, drawing, and quantity',
  3: 'Material specifications and manufacturing processes',
  4: 'Tolerances, GD&T, certifications, and inspection',
  5: 'Delivery timeline, Incoterms, pricing, and special notes',
};

export default function RFQPage() {
  const router = useRouter();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<RFQFormData>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rfqRef, setRfqRef] = useState('');
  const [error, setError] = useState('');
  const formStartedRef = useRef(false);

  // Track form start once on mount
  useEffect(() => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      rfqAnalytics.formStart();
    }
  }, []);

  const update = (field: keyof RFQFormData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleArray = (field: 'manufacturingProcesses' | 'requiredCertifications', value: string) => {
    setForm(prev => {
      const arr = prev[field] as string[];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const validateStep = (): boolean => {
    if (step === 1) {
      if (!form.contactName.trim()) { setError('Contact name is required.'); return false; }
      if (!form.contactEmail.trim() || !/\S+@\S+\.\S+/.test(form.contactEmail)) { setError('Valid email is required.'); return false; }
      if (!form.companyName.trim()) { setError('Company name is required.'); return false; }
      if (!form.country) { setError('Country is required.'); return false; }
    }
    if (step === 2) {
      if (!form.partName.trim()) { setError('Part / component name is required.'); return false; }
      if (!form.quantityRequired || isNaN(Number(form.quantityRequired)) || Number(form.quantityRequired) <= 0) {
        setError('Valid quantity is required.'); return false;
      }
    }
    if (step === 3) {
      if (!form.materialType) { setError('Material type is required.'); return false; }
      if (form.manufacturingProcesses.length === 0) { setError('Select at least one manufacturing process.'); return false; }
    }
    setError('');
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    rfqAnalytics.stepComplete(step, stepTitles[step]);
    setStep(prev => Math.min(prev + 1, 5) as Step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setError('');
    rfqAnalytics.stepBack(step, stepTitles[step]);
    setStep(prev => Math.max(prev - 1, 1) as Step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    setError('');

    try {
      const supabase = createClient();
      const rfqReference = `RFQ-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

      const { error: dbError } = await supabase.from('rfq_submissions').insert({
        rfq_reference: rfqReference,
        contact_name: form.contactName,
        contact_email: form.contactEmail,
        contact_phone: form.contactPhone || null,
        contact_designation: form.contactDesignation || null,
        company_name: form.companyName,
        company_website: form.companyWebsite || null,
        country: form.country,
        part_name: form.partName,
        part_number: form.partNumber || null,
        drawing_revision: form.drawingRevision || null,
        quantity_required: parseInt(form.quantityRequired),
        unit_of_measure: form.unitOfMeasure,
        drawing_available: form.drawingAvailable,
        drawing_format: form.drawingFormat || null,
        material_type: form.materialType,
        material_grade: form.materialGrade || null,
        manufacturing_processes: form.manufacturingProcesses,
        surface_finish: form.surfaceFinish || null,
        heat_treatment: form.heatTreatment || null,
        general_tolerance: form.generalTolerance || null,
        critical_dimensions: form.criticalDimensions || null,
        geometric_tolerances: form.geometricTolerances || null,
        inspection_requirements: form.inspectionRequirements || null,
        required_certifications: form.requiredCertifications,
        quality_standard: form.qualityStandard || null,
        first_article_required: form.firstArticleRequired,
        material_certification_required: form.materialCertificationRequired,
        required_delivery_date: form.requiredDeliveryDate || null,
        delivery_terms: form.deliveryTerms || null,
        delivery_location: form.deliveryLocation || null,
        urgency_level: form.urgencyLevel,
        target_unit_price: form.targetUnitPrice ? parseFloat(form.targetUnitPrice) : null,
        currency: form.currency,
        annual_volume_estimate: form.annualVolumeEstimate ? parseInt(form.annualVolumeEstimate) : null,
        special_requirements: form.specialRequirements || null,
        packaging_requirements: form.packagingRequirements || null,
        additional_notes: form.additionalNotes || null,
        status: 'new',
      });

      if (dbError) throw new Error(dbError.message);

      // Track step 5 completion then successful submission
      rfqAnalytics.stepComplete(5, stepTitles[5]);
      rfqAnalytics.formSubmit(rfqReference, form.urgencyLevel, form.country);

      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      try {
        await fetch(`${supabaseUrl}/functions/v1/send-rfq-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rfqReference,
            contactName: form.contactName,
            contactEmail: form.contactEmail,
            contactPhone: form.contactPhone,
            contactDesignation: form.contactDesignation,
            companyName: form.companyName,
            companyWebsite: form.companyWebsite,
            country: form.country,
            partName: form.partName,
            partNumber: form.partNumber,
            drawingRevision: form.drawingRevision,
            quantityRequired: form.quantityRequired,
            unitOfMeasure: form.unitOfMeasure,
            materialType: form.materialType,
            materialGrade: form.materialGrade,
            manufacturingProcesses: form.manufacturingProcesses,
            surfaceFinish: form.surfaceFinish,
            heatTreatment: form.heatTreatment,
            generalTolerance: form.generalTolerance,
            criticalDimensions: form.criticalDimensions,
            geometricTolerances: form.geometricTolerances,
            inspectionRequirements: form.inspectionRequirements,
            requiredCertifications: form.requiredCertifications,
            qualityStandard: form.qualityStandard,
            firstArticleRequired: form.firstArticleRequired,
            materialCertificationRequired: form.materialCertificationRequired,
            requiredDeliveryDate: form.requiredDeliveryDate,
            deliveryTerms: form.deliveryTerms,
            deliveryLocation: form.deliveryLocation,
            urgencyLevel: form.urgencyLevel,
            targetUnitPrice: form.targetUnitPrice,
            currency: form.currency,
            annualVolumeEstimate: form.annualVolumeEstimate,
            drawingAvailable: form.drawingAvailable,
            drawingFormat: form.drawingFormat,
            specialRequirements: form.specialRequirements,
            packagingRequirements: form.packagingRequirements,
            additionalNotes: form.additionalNotes,
          }),
        });
      } catch {
        // Email failure is non-blocking
      }

      setRfqRef(rfqReference);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      rfqAnalytics.submitError(err.message || 'Unknown error');
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground text-sm placeholder-technical-gray/50 focus:outline-none focus:border-accent transition-colors duration-200';
  const labelCls = 'block text-xs font-medium text-technical-gray mb-1.5 uppercase tracking-wide';
  const requiredMark = <span className="text-mission-orange ml-0.5">*</span>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary to-secondary">
      {/* Animated background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden opacity-10">
        <div className="absolute top-20 left-10 w-96 h-96 bg-accent rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-mission-orange rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Top Nav Bar */}
      <div className="sticky top-0 z-40 bg-card/90 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/homepage" className="flex items-center space-x-2 group">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center group-hover:bg-accent/20 transition-colors duration-200">
              <Icon name="ArrowLeftIcon" size={16} className="text-technical-gray group-hover:text-accent transition-colors duration-200" />
            </div>
            <span className="font-body text-sm text-technical-gray group-hover:text-accent transition-colors duration-200">Back to Home</span>
          </Link>

          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-mission-orange/20 flex items-center justify-center">
              <Icon name="DocumentTextIcon" size={16} className="text-mission-orange" />
            </div>
            <div className="hidden sm:block">
              <p className="font-headline text-sm font-bold text-foreground">Request for Quotation</p>
              <p className="font-body text-xs text-technical-gray">Wings Bharath — Advanced Manufacturing RFQ Portal</p>
            </div>
          </div>

          <div className="flex items-center space-x-2 px-3 py-1.5 bg-muted rounded-lg border border-border">
            <span className="w-1.5 h-1.5 rounded-full bg-aerospace-green animate-pulse" />
            <span className="font-cta text-xs text-aerospace-green tracking-widest">SECURE PORTAL</span>
          </div>
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-10">
        {submitted ? (
          /* ── Success State ── */
          <div className="flex flex-col items-center justify-center min-h-[70vh] text-center space-y-8">
            <div className="w-24 h-24 rounded-full bg-aerospace-green/20 flex items-center justify-center ring-4 ring-aerospace-green/20">
              <Icon name="CheckBadgeIcon" size={52} className="text-aerospace-green" />
            </div>
            <div>
              <h2 className="font-headline text-4xl font-bold text-foreground mb-3">RFQ Submitted Successfully!</h2>
              <p className="font-body text-lg text-technical-gray max-w-xl mx-auto">
                Your Request for Quotation has been received. Our engineering team will review your requirements and respond within{' '}
                <strong className="text-foreground">24–48 business hours</strong>.
              </p>
            </div>
            <div className="bg-card border border-border rounded-2xl px-12 py-6 space-y-2">
              <p className="font-body text-xs text-technical-gray uppercase tracking-widest">Your RFQ Reference Number</p>
              <p className="font-headline text-3xl font-bold text-mission-orange tracking-widest">{rfqRef}</p>
              <p className="font-body text-xs text-technical-gray">Save this reference for tracking your quotation status</p>
            </div>
            <p className="font-body text-sm text-technical-gray">
              A confirmation has been sent to <strong className="text-foreground">{form.contactEmail}</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/homepage"
                className="inline-flex items-center space-x-2 px-8 py-3 bg-mission-orange text-primary font-cta text-sm font-medium rounded-lg hover:shadow-glow transition-all duration-300"
              >
                <Icon name="HomeIcon" size={16} />
                <span>BACK TO HOME</span>
              </Link>
              <button
                onClick={() => { setSubmitted(false); setStep(1); setForm(initialForm); setRfqRef(''); }}
                className="inline-flex items-center space-x-2 px-8 py-3 border border-border text-technical-gray font-cta text-sm font-medium rounded-lg hover:border-accent hover:text-accent transition-all duration-300"
              >
                <Icon name="PlusIcon" size={16} />
                <span>SUBMIT ANOTHER RFQ</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[280px_1fr] gap-8">
            {/* ── Left Sidebar: Step Navigator ── */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-2">
                <p className="font-cta text-xs text-technical-gray tracking-widest uppercase mb-4 px-2">Progress</p>
                {([1, 2, 3, 4, 5] as Step[]).map((s) => (
                  <div
                    key={s}
                    className={`flex items-start space-x-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      s === step
                        ? 'bg-mission-orange/10 border border-mission-orange/30'
                        : s < step
                        ? 'bg-accent/5 border border-accent/20' :'border border-transparent'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      s < step ? 'bg-accent border-accent' : s === step ? 'border-mission-orange bg-mission-orange/10' : 'border-border bg-muted'
                    }`}>
                      {s < step ? (
                        <Icon name="CheckIcon" size={14} className="text-primary" />
                      ) : (
                        <Icon name={stepIcons[s] as any} size={14} className={s === step ? 'text-mission-orange' : 'text-technical-gray'} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className={`font-cta text-xs font-medium transition-colors duration-200 ${
                        s === step ? 'text-mission-orange' : s < step ? 'text-accent' : 'text-technical-gray'
                      }`}>
                        STEP {s}
                      </p>
                      <p className={`font-body text-sm transition-colors duration-200 ${
                        s === step ? 'text-foreground font-medium' : s < step ? 'text-foreground/70' : 'text-technical-gray'
                      }`}>
                        {stepTitles[s]}
                      </p>
                      {s === step && (
                        <p className="font-body text-xs text-technical-gray mt-0.5 leading-relaxed">{stepDescriptions[s]}</p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Info box */}
                <div className="mt-6 p-4 bg-muted/50 border border-border rounded-xl space-y-2">
                  <div className="flex items-center space-x-2">
                    <Icon name="ClockIcon" size={14} className="text-accent" />
                    <span className="font-cta text-xs text-accent">24–48 hr response</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="ShieldCheckIcon" size={14} className="text-aerospace-green" />
                    <span className="font-cta text-xs text-aerospace-green">AS9100D Certified</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="LockClosedIcon" size={14} className="text-technical-gray" />
                    <span className="font-cta text-xs text-technical-gray">Secure & Confidential</span>
                  </div>
                </div>
              </div>
            </aside>

            {/* ── Main Form Area ── */}
            <div className="space-y-6">
              {/* Mobile step indicator */}
              <div className="lg:hidden flex items-center space-x-1 mb-2">
                {([1, 2, 3, 4, 5] as Step[]).map((s) => (
                  <div key={s} className="flex items-center flex-1">
                    <div className={`flex items-center justify-center w-7 h-7 rounded-full border-2 transition-all duration-300 flex-shrink-0 ${
                      s < step ? 'bg-accent border-accent' : s === step ? 'border-mission-orange bg-mission-orange/10' : 'border-border bg-muted'
                    }`}>
                      {s < step ? (
                        <Icon name="CheckIcon" size={12} className="text-primary" />
                      ) : (
                        <Icon name={stepIcons[s] as any} size={12} className={s === step ? 'text-mission-orange' : 'text-technical-gray'} />
                      )}
                    </div>
                    {s < 5 && <div className={`flex-1 h-0.5 mx-1 transition-all duration-300 ${s < step ? 'bg-accent' : 'bg-border'}`} />}
                  </div>
                ))}
              </div>

              {/* Step Header */}
              <div className="bg-card border border-border rounded-2xl px-8 py-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-mission-orange/20 flex items-center justify-center flex-shrink-0">
                    <Icon name={stepIcons[step] as any} size={24} className="text-mission-orange" />
                  </div>
                  <div>
                    <p className="font-cta text-xs text-mission-orange tracking-widest">STEP {step} OF 5</p>
                    <h1 className="font-headline text-2xl font-bold text-foreground">{stepTitles[step]}</h1>
                    <p className="font-body text-sm text-technical-gray mt-0.5">{stepDescriptions[step]}</p>
                  </div>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center space-x-2 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <Icon name="ExclamationTriangleIcon" size={16} className="text-red-400 flex-shrink-0" />
                  <p className="font-body text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Form Card */}
              <div className="bg-card border border-border rounded-2xl px-8 py-8">

                {/* STEP 1 */}
                {step === 1 && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>Full Name {requiredMark}</label>
                        <input className={inputCls} placeholder="John Smith" value={form.contactName} onChange={e => update('contactName', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Designation / Title</label>
                        <input className={inputCls} placeholder="Procurement Manager" value={form.contactDesignation} onChange={e => update('contactDesignation', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Business Email {requiredMark}</label>
                        <input className={inputCls} type="email" placeholder="john@company.com" value={form.contactEmail} onChange={e => update('contactEmail', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Phone / WhatsApp</label>
                        <input className={inputCls} placeholder="+1 555 000 0000" value={form.contactPhone} onChange={e => update('contactPhone', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Company Name {requiredMark}</label>
                        <input className={inputCls} placeholder="Acme Aerospace Inc." value={form.companyName} onChange={e => update('companyName', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Company Website</label>
                        <input className={inputCls} placeholder="https://company.com" value={form.companyWebsite} onChange={e => update('companyWebsite', e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Country / Region {requiredMark}</label>
                        <select className={inputCls} value={form.country} onChange={e => update('country', e.target.value)}>
                          <option value="">Select country...</option>
                          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Part / Component Name {requiredMark}</label>
                        <input className={inputCls} placeholder="e.g. Turbine Blade Bracket, Structural Frame Assembly" value={form.partName} onChange={e => update('partName', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Part Number / Drawing No.</label>
                        <input className={inputCls} placeholder="e.g. PN-2024-001-A" value={form.partNumber} onChange={e => update('partNumber', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Drawing Revision</label>
                        <input className={inputCls} placeholder="e.g. Rev C, Issue 3" value={form.drawingRevision} onChange={e => update('drawingRevision', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Quantity Required {requiredMark}</label>
                        <input className={inputCls} type="number" min="1" placeholder="e.g. 50" value={form.quantityRequired} onChange={e => update('quantityRequired', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Unit of Measure</label>
                        <select className={inputCls} value={form.unitOfMeasure} onChange={e => update('unitOfMeasure', e.target.value)}>
                          {['pieces', 'sets', 'assemblies', 'kg', 'meters', 'lots'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="bg-muted/50 border border-border rounded-xl p-5 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-cta text-sm text-foreground">Technical Drawing / CAD File Available?</p>
                          <p className="font-body text-xs text-technical-gray mt-0.5">Drawings can be shared via email after submission</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => update('drawingAvailable', !form.drawingAvailable)}
                          className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${form.drawingAvailable ? 'bg-accent' : 'bg-border'}`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${form.drawingAvailable ? 'translate-x-6' : ''}`} />
                        </button>
                      </div>
                      {form.drawingAvailable && (
                        <div>
                          <label className={labelCls}>Drawing Format</label>
                          <select className={inputCls} value={form.drawingFormat} onChange={e => update('drawingFormat', e.target.value)}>
                            <option value="">Select format...</option>
                            {['PDF', 'DXF', 'DWG', 'STEP / STP', 'IGES', 'Solidworks (.sldprt)', 'CATIA', 'NX / Unigraphics', 'Other'].map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* STEP 3 */}
                {step === 3 && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>Material Type {requiredMark}</label>
                        <select className={inputCls} value={form.materialType} onChange={e => update('materialType', e.target.value)}>
                          <option value="">Select material...</option>
                          {MATERIAL_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Material Grade / Specification</label>
                        <input className={inputCls} placeholder="e.g. Ti-6Al-4V, 7075-T6, 316L, AMS 4928" value={form.materialGrade} onChange={e => update('materialGrade', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Surface Finish</label>
                        <input className={inputCls} placeholder="e.g. Ra 0.8 µm, Hard Anodize, Chromate Coat" value={form.surfaceFinish} onChange={e => update('surfaceFinish', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Heat Treatment</label>
                        <input className={inputCls} placeholder="e.g. T6 Temper, Stress Relief, Annealing" value={form.heatTreatment} onChange={e => update('heatTreatment', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Manufacturing Processes Required {requiredMark}</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                        {MANUFACTURING_PROCESSES.map(p => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => toggleArray('manufacturingProcesses', p)}
                            className={`px-3 py-2.5 rounded-lg text-xs font-cta text-left transition-all duration-200 border ${
                              form.manufacturingProcesses.includes(p)
                                ? 'bg-accent/20 border-accent text-accent' :'bg-muted border-border text-technical-gray hover:border-accent/50'
                            }`}
                          >
                            {form.manufacturingProcesses.includes(p) && <span className="mr-1">✓</span>}
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 4 */}
                {step === 4 && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>General Tolerance</label>
                        <input className={inputCls} placeholder="e.g. ±0.05 mm, ISO 2768-m, DIN 7168" value={form.generalTolerance} onChange={e => update('generalTolerance', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Quality Standard</label>
                        <input className={inputCls} placeholder="e.g. AS9100D, MIL-STD-1916, ASTM" value={form.qualityStandard} onChange={e => update('qualityStandard', e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Critical Dimensions / Tight Tolerances</label>
                        <textarea className={`${inputCls} resize-none`} rows={3} placeholder="Describe any critical dimensions, tight tolerances, or key characteristics (e.g. Bore Ø 25.000 ±0.005 mm, flatness 0.01 mm)" value={form.criticalDimensions} onChange={e => update('criticalDimensions', e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>GD&T / Geometric Tolerances</label>
                        <textarea className={`${inputCls} resize-none`} rows={3} placeholder="e.g. Perpendicularity ⊥ 0.02 mm, Runout ↗ 0.03 mm, Position ⊕ Ø0.1 mm" value={form.geometricTolerances} onChange={e => update('geometricTolerances', e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Inspection & Testing Requirements</label>
                        <textarea className={`${inputCls} resize-none`} rows={3} placeholder="e.g. CMM inspection, NDT (X-ray / UT), Pressure test, Hardness test, Dimensional report" value={form.inspectionRequirements} onChange={e => update('inspectionRequirements', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Required Certifications / Compliance</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                        {CERTIFICATIONS.map(c => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => toggleArray('requiredCertifications', c)}
                            className={`px-3 py-2.5 rounded-lg text-xs font-cta text-left transition-all duration-200 border ${
                              form.requiredCertifications.includes(c)
                                ? 'bg-accent/20 border-accent text-accent' :'bg-muted border-border text-technical-gray hover:border-accent/50'
                            }`}
                          >
                            {form.requiredCertifications.includes(c) && <span className="mr-1">✓</span>}
                            {c}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { field: 'firstArticleRequired' as const, label: 'First Article Inspection (FAI) Required', desc: 'AS9102 / PPAP first article report' },
                        { field: 'materialCertificationRequired' as const, label: 'Material Test Certificate Required', desc: 'Mill cert / CoC with traceability' },
                      ].map(({ field, label, desc }) => (
                        <div key={field} className="flex items-center justify-between bg-muted/50 border border-border rounded-xl p-4">
                          <div>
                            <p className="font-cta text-sm text-foreground">{label}</p>
                            <p className="font-body text-xs text-technical-gray mt-0.5">{desc}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => update(field, !form[field])}
                            className={`relative w-12 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ml-3 ${form[field] ? 'bg-accent' : 'bg-border'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${form[field] ? 'translate-x-6' : ''}`} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* STEP 5 */}
                {step === 5 && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>Required Delivery Date</label>
                        <input className={inputCls} type="date" value={form.requiredDeliveryDate} onChange={e => update('requiredDeliveryDate', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Urgency Level</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: 'standard', label: 'Standard', color: 'text-aerospace-green border-aerospace-green bg-aerospace-green/10' },
                            { value: 'high', label: 'High', color: 'text-mission-orange border-mission-orange bg-mission-orange/10' },
                            { value: 'urgent', label: 'Urgent', color: 'text-red-400 border-red-400 bg-red-400/10' },
                          ].map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => update('urgencyLevel', opt.value)}
                              className={`py-3 rounded-lg text-xs font-cta border-2 transition-all duration-200 ${
                                form.urgencyLevel === opt.value ? opt.color : 'border-border text-technical-gray bg-muted'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Delivery Terms (Incoterms)</label>
                        <select className={inputCls} value={form.deliveryTerms} onChange={e => update('deliveryTerms', e.target.value)}>
                          <option value="">Select terms...</option>
                          {['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DDP', 'FOB', 'CIF', 'CFR'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Delivery Location / Port</label>
                        <input className={inputCls} placeholder="e.g. Los Angeles, CA, USA" value={form.deliveryLocation} onChange={e => update('deliveryLocation', e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Target Unit Price</label>
                        <div className="flex space-x-2">
                          <select className={`${inputCls} w-24 flex-shrink-0`} value={form.currency} onChange={e => update('currency', e.target.value)}>
                            {['USD', 'EUR', 'GBP', 'INR', 'JPY', 'AUD', 'SGD'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00" value={form.targetUnitPrice} onChange={e => update('targetUnitPrice', e.target.value)} />
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Annual Volume Estimate</label>
                        <input className={inputCls} type="number" min="0" placeholder="e.g. 1200 pieces/year" value={form.annualVolumeEstimate} onChange={e => update('annualVolumeEstimate', e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Special / Regulatory Requirements</label>
                        <textarea className={`${inputCls} resize-none`} rows={3} placeholder="e.g. ITAR controlled, export license required, specific handling, cleanliness class" value={form.specialRequirements} onChange={e => update('specialRequirements', e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Packaging Requirements</label>
                        <textarea className={`${inputCls} resize-none`} rows={3} placeholder="e.g. Individual poly bags, foam-lined cases, vacuum sealed, ESD packaging" value={form.packagingRequirements} onChange={e => update('packagingRequirements', e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelCls}>Additional Notes / Context</label>
                        <textarea className={`${inputCls} resize-none`} rows={4} placeholder="Any other information that will help us prepare an accurate quotation..." value={form.additionalNotes} onChange={e => update('additionalNotes', e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Footer */}
              <div className="flex items-center justify-between bg-card border border-border rounded-2xl px-8 py-5">
                <button
                  onClick={back}
                  disabled={step === 1}
                  className="flex items-center space-x-2 px-6 py-3 rounded-lg border border-border text-technical-gray font-cta text-sm hover:border-accent hover:text-accent transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeftIcon" size={16} />
                  <span>BACK</span>
                </button>

                <div className="flex items-center space-x-1.5">
                  {([1, 2, 3, 4, 5] as Step[]).map(s => (
                    <div key={s} className={`rounded-full transition-all duration-300 ${s === step ? 'bg-mission-orange w-5 h-2' : s < step ? 'bg-accent w-2 h-2' : 'bg-border w-2 h-2'}`} />
                  ))}
                </div>

                {step < 5 ? (
                  <button
                    onClick={next}
                    className="flex items-center space-x-2 px-8 py-3 rounded-lg bg-mission-orange text-primary font-cta text-sm font-medium hover:shadow-glow transition-all duration-300"
                  >
                    <span>NEXT</span>
                    <Icon name="ChevronRightIcon" size={16} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex items-center space-x-2 px-8 py-3 rounded-lg bg-mission-orange text-primary font-cta text-sm font-medium hover:shadow-glow transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        <span>SUBMITTING...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="PaperAirplaneIcon" size={16} />
                        <span>SUBMIT RFQ</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
